// Modules
import React, { useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";

// Context
import ApiContext from "../../contexts/ApiContext";
import config from "../../config/environment";

// Hooks
import usePrevious from "../hooks/usePrevious";

// Local
import Divider from "../utils/Divider";
import Simulator from "./Simulator";
import Results from "./Results";


const BalanceSheet = (props) => {
	const {
		idFiche,
		data,
		loader,
	} = props;
	const { isMobile, isTablet } = useContext(ApiContext);

	// Usual state variables
	const [error, setError] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [goFetch, setGoFetch] = useState(false);

	// Variables essentials to the simulation
	const [valuesAPI, setValuesAPI] = useState({
		bilan_mensuel: 0,
		gain_net_revente: 0,
		mensualite: 0,
		montant_emprunte: 0,
		prix_revente: 0,
		rente_nette: 0,
		total_acquisition: 0,
		total_depenses_mois: 0,
		graphs: {
			comparatif_livret_a: {
				data: [],
				layout: {},
			},
			comparatif_mensualite_loyer: {},
			evolution_gain_net: {
				data: [],
				layout: {
					shapes: [{}],
				},
			},
			levier_bancaire: {
				data: [],
				layout: {},
			},
		},
	});
	const prevValuesAPI = usePrevious(valuesAPI);
	const [valuesSimulation, setValuesSimulation] = useState({
		frais_courtage: 2000,
		mise_depart: 20000,
		duree_emprunt: 20,
		taux_emprunt: config.SIMULATION.taux_emprunt,
		taux_assurance: config.SIMULATION.taux_assurance,
		duree_revente: 20,
	});
	const prevValuesSimulation = usePrevious(valuesSimulation);
	const [stateSwitch, setStateSwitch] = useState({
		fraisCourtage: true,
		effortEpargne: true,
		equilibre: true,
	});
	// V2
	// const prevStateSwitch = usePrevious(stateSwitch);
	const [hasDataChanged, setHasDataChanged] = useState({
		bilan_mensuel: false,
		gain_net_revente: false,
		mensualite: false,
		montant_emprunte: false,
		prix_revente: false,
		rente_nette: false,
		total_acquisition: false,
		total_depenses_mois: false,
		frais_courtage: false,
		mise_depart: false,
		duree_emprunt: false,
		taux_emprunt: false,
		taux_assurance: false,
		duree_revente: false,
		fraisCourtage: false,
		effortEpargne: false,
		equilibre: false,
	});
	const timeout = useRef(null);

	/**
	 * Triggered individually for each object of data linked to the API that just changed
	 * (currently valuesAPI & valuesSimulation).
	 * Updates the state of all the items stored in an object together.
	 * @param {Object} dataObject : new data fresh out of API
	 * @param {Object} prevDataObject : saved version of same data before going through the last API call
	 * @returns 
	 */
	const handleDataHasChanged = (dataObject, prevDataObject) => {
		if (isInitialized === false)
			return;

		let newHasDataChanged = { ...hasDataChanged };

		for (const [key, value] of Object.entries(dataObject))
			for (const [prevKey, prevValue] of Object.entries(prevDataObject))
				if (key === prevKey && value !== prevValue && key !== "graphs")
					newHasDataChanged[key] = true;

		setHasDataChanged(newHasDataChanged);
		timeout.current = setTimeout(() => {
			if (timeout.current !== null)
				setHasDataChanged({
					bilan_mensuel: false,
					gain_net_revente: false,
					mensualite: false,
					montant_emprunte: false,
					prix_revente: false,
					rente_nette: false,
					total_acquisition: false,
					total_depenses_mois: false,
					frais_courtage: false,
					mise_depart: false,
					duree_emprunt: false,
					taux_emprunt: false,
					taux_assurance: false,
					duree_revente: false,
					fraisCourtage: false,
					effortEpargne: false,
					equilibre: false,
				});
		}, 3000);
	};

	/**
	 * Each time valuesAPI changes, trigger the function that updates the state 
	 * of changed items.
	 */
	useEffect(() => {
		handleDataHasChanged(valuesAPI, prevValuesAPI);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valuesAPI]);

	/**
	 * Each time valuesSimulation changes, trigger the function that updates the state 
	 * of changed items.
	 */
	useEffect(() => {
		handleDataHasChanged(valuesSimulation, prevValuesSimulation);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valuesSimulation]);

	/**
	 * Triggered at each API call to the simulation.
	 * Parse the objects it sends for the graphs.
	 * @param {*} graphs : fresh out of API graph's object
	 * @returns {Object} : parsed graph's object
	 */
	const handleParseGraphs = (graphs) => {
		let copySavedGraphs = { ...valuesAPI.graphs };
		const graphsContainer = document.getElementById("graphs-container");
		const vh = parseFloat(document.documentElement.style.getPropertyValue("--vh").replace("px", ""));

		Object.entries(copySavedGraphs).map((graph) => {
			const key = graph[0];
			const object = graph[1];
			let newObject = {
				data: graphs[key].data,
				layout: {},
			};

			if (key !== "comparatif_mensualite_loyer") {
				if (object.layout.height === undefined || object.layout.width === undefined)
					newObject.layout = {
						width: isMobile === true || isTablet === true ? graphsContainer.offsetWidth + 100 : graphsContainer.offsetWidth - 100,
						height: 50 * vh,
						...graphs[key].layout,
					};
				else
					newObject.layout = {
						...object.layout,
						...graphs[key].layout,
					};
			}
			else
				newObject = graphs[key];

			copySavedGraphs[key] = newObject;
			return newObject;
		});

		return copySavedGraphs;
	};

	/**
	 * Implementation in V2 for multiple switches
	 */
	// useEffect(() => {
	// 	handleDataHasChanged(stateSwitch, prevStateSwitch);
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [stateSwitch]);

	/**
	 * When idFiche is present, calls the API to get
	 * the initial values.
	 * After that, whenever stateSwitch or valuesSimulation
	 * change, asks API for new calculations.
	 */
	useEffect(() => {
		/**
		 * Sends to the API the variables that can be modified by the user.
		 * Sets the computed results sent by the API.
		 */
		const fetchData = async () => {
			try {
				if (idFiche !== "" && goFetch === true) {
					const newValues = {
						id: idFiche,
						frais_courtage: valuesSimulation.frais_courtage,
						mise_depart: valuesSimulation.mise_depart,
						duree_emprunt: valuesSimulation.duree_emprunt,
						taux_emprunt: valuesSimulation.taux_emprunt,
						taux_assurance: valuesSimulation.taux_assurance,
						duree_revente: valuesSimulation.duree_revente,
						graphs: true,
					};
					const result = await axios.post(config.API_SIMULATION_URL, newValues);
					const parsedGraphs = handleParseGraphs(result?.data?.graphs);

					if (typeof (result?.data) === "object") {
						setValuesAPI({
							...result.data,
							graphs: parsedGraphs,
						});
						setError(false);
					}
					else
						setError(true);

					if (isInitialized === false)
						setIsInitialized(true);

					if (result.status !== 200) {
						setError(true);
						console.error("Une erreur est survenue", result.status);
					}
				}
			}
			catch (err) {
				console.error("Une erreur est survenue", err);
				setError(true);
			}
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [goFetch, idFiche, stateSwitch, valuesSimulation]);

	/**
	 * Prevents the first fetch to the API as long aw we didn't receive the total amount
	 * of the project (total_acquisition) so that the mise_depart is correctly initialized, 
	 * along with all of the values depending on it.
	 */
	useEffect(() => {
		if (data?.total_acquisition && goFetch === false) {
			setValuesSimulation({ ...valuesSimulation, mise_depart: (data.total_acquisition / 100) * config.SIMULATION.mise_depart_percent });
			setGoFetch(true);
		}
	}, [data.total_acquisition, goFetch]); // eslint-disable-line

	useEffect(() => {
		return () => clearTimeout(timeout.current);
	}, []);

	return (
		<React.Fragment>
			<Divider text="Bilan financier" loader={loader} />
			<div className={`balance-sheet-global-container ${isMobile === true || isTablet === true ? "mobile" : ""}`}>
				<Simulator
					loader={loader}
					error={error}
					valuesSimulation={valuesSimulation}
					setValuesSimulation={setValuesSimulation}
					stateSwitch={stateSwitch}
					setStateSwitch={setStateSwitch}
					total_acquisition={data.total_acquisition}
				/>
				<Results
					data={data}
					loader={loader}
					valuesAPI={valuesAPI}
					valuesSimulation={valuesSimulation}
					hasDataChanged={hasDataChanged}
					fraisCourtageSwitch={stateSwitch.fraisCourtage}
				/>
			</div>
		</React.Fragment>
	);
};

BalanceSheet.propTypes = {
	idFiche: PropTypes.string.isRequired,
	data: PropTypes.object.isRequired,
	loader: PropTypes.bool.isRequired,
};

export default BalanceSheet;